import {
	Option,
	ShopRole,
	ShopRoles,
	Permission,
	Permissions,
	EnumValues,
	UserApi,
	Role,
} from 'common/types';
import { stringEnum } from 'common/utils/objects';
import {
	isAdmin,
	hasLowerAccess,
	getUserAccessClaims,
	getAllPermissionsForUser,
	getShopRoles,
	hasAccessToAllLocations,
	hasAccessToLocation,
	isRentleSupportFromUser,
	canViewUser,
} from 'common/modules/users/utils';
import { t } from 'services/localization';
import { isEmpty, sortBy } from 'lodash';
import { switchUnreachable } from 'common/utils/common';

export interface UsersTableFilters {
	roles: Role[];
	permissions: Permission[];
	locations: string[];
	search: string;
}

export const getUsersToShow = (args: {
	users: UserApi[];
	activeUser: UserApi;
	shopId: string;
}): UserApi[] => {
	const { users, activeUser, shopId } = args;
	const activeUserAccess = getUserAccessClaims(activeUser, shopId);
	if (!activeUserAccess) return [];
	if (isRentleSupportFromUser(activeUser, shopId)) return users;

	return users.filter((user) => {
		const userAccess = getUserAccessClaims(user, shopId);
		if (!userAccess) return false;
		return canViewUser(activeUserAccess, userAccess);
	});
};

export const getFilteredUsers = (args: {
	users: UserApi[];
	filters: UsersTableFilters;
	shopId: string;
}): UserApi[] => {
	const { users, filters, shopId } = args;
	return users.filter((user) => {
		const accessClaims = getUserAccessClaims(user, shopId);
		if (!accessClaims) return false;

		const userRoles = getShopRoles(accessClaims.roles);
		const userPermissions = getAllPermissionsForUser(accessClaims);
		const trimmedSearch = filters.search.trim().toLowerCase();

		const matchesSearch = !trimmedSearch || user.email.toLowerCase().indexOf(trimmedSearch) !== -1;
		if (!matchesSearch) return false;

		const hasOneOfRoles =
			isEmpty(filters.roles) || filters.roles.some((role) => userRoles.includes(role as ShopRole));
		if (!hasOneOfRoles) return false;

		const hasOneOfPermissions =
			isEmpty(filters.permissions) ||
			filters.permissions.some((permission) => userPermissions.includes(permission));
		if (!hasOneOfPermissions) return false;

		const hasOneOfLocations =
			isEmpty(filters.locations) ||
			filters.locations.some((location) => {
				if (location === 'ALL') {
					return hasAccessToAllLocations(accessClaims);
				} else {
					return hasAccessToLocation(accessClaims, location);
				}
			});

		if (!hasOneOfLocations) return false;

		return true;
	});
};

export const getRoleText = (role: ShopRole) => {
	switch (role) {
		case ShopRoles.admin:
			return t('users.admin', 'Admin');
		case ShopRoles.manager:
			return t('users.manager', 'Manager');
		case ShopRoles.editor:
			return t('users.editor', 'Editor');
		default:
			return t('users.member', 'Member');
	}
};

export interface RoleOption extends Option<ShopRole> {
	description: string;
}

export const getRoleOptions = (userRoles?: ShopRole[]): RoleOption[] => {
	const allRoleOptions = [
		{
			value: ShopRoles.admin,
			label: getRoleText(ShopRoles.admin),
			description: t('users.adminDescription', 'Full access.'),
		},
		{
			value: ShopRoles.manager,
			label: getRoleText(ShopRoles.manager),
			description: t(
				'users.managerDescription',
				'Full access, but not allowed to edit plan & billing.',
			),
		},
		{
			value: ShopRoles.editor,
			label: getRoleText(ShopRoles.editor),
			description: t(
				'users.editorDescription2',
				'Create, view and edit products, orders and translations.',
			),
		},
		{
			value: ShopRoles.member,
			label: getRoleText(ShopRoles.member),
			description: t('users.memberDescription2', 'Create, view and edit orders.'),
		},
	];

	if (isAdmin(userRoles ?? [])) return allRoleOptions;

	return userRoles
		? allRoleOptions.filter((option) => hasLowerAccess([option.value], userRoles))
		: allRoleOptions;
};

export const getPermissionText = (permission: Permission) => {
	switch (permission) {
		case 'modify_payments':
			return t('users.payments', 'Payments');
		case 'allow_overbookings':
			return t('users.overbookings', 'Overbookings');
		case 'edit_booking':
			return t('users.editBooking', 'Edit order');
		case 'cancel_booking':
			return t('users.cancelBooking', 'Cancel order');
		default:
			return switchUnreachable(permission);
	}
};

export interface PermissionOption extends Option<Permission> {
	description: string;
}

export const getPermissionOptions = (): PermissionOption[] => [
	{
		value: Permissions.modify_payments,
		label: getPermissionText(Permissions.modify_payments),
		description: t(
			'users.paymentsDescription',
			'Allow refunding user payments, or capture reserved deposits.',
		),
	},
	{
		value: Permissions.allow_overbookings,
		label: getPermissionText(Permissions.allow_overbookings),
		description: t(
			'users.overbookingsDescription2',
			'Allow creating overbookings and orders outside of opening hours',
		),
	},
	{
		value: Permissions.edit_booking,
		label: getPermissionText(Permissions.edit_booking),
		description: t(
			'users.editBookingDescription',
			'Allow editing booking contents, prices and schedules',
		),
	},
	{
		value: Permissions.cancel_booking,
		label: getPermissionText(Permissions.cancel_booking),
		description: t('users.cancelBookingDescription', 'Allow cancelling a booking'),
	},
];

export const SortDirections = stringEnum(['asc', 'desc']);
export type SortDirection = EnumValues<typeof SortDirections>;
export const SortKeys = stringEnum(['email']);
export type SortKey = EnumValues<typeof SortKeys>;
export type SortObject = { [key in SortKey]: number | string };

export const getSortedUsers = (args: {
	users: UserApi[];
	sortDirection: SortDirection;
	sortKey: SortKey;
}): UserApi[] => {
	const sorted = sortBy(args.users, args.sortKey);
	return args.sortDirection === 'asc' ? sorted : sorted.reverse();
};

export const hasOnlyOneAdmin = (userRoles: Role[], users: UserApi[], shopId: string) => {
	const adminUsers = users.filter((user) => isAdmin(user.access?.[shopId].roles ?? []));
	return isAdmin(userRoles) && adminUsers.length === 1;
};
